import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBadgeCheck } from '@fortawesome/pro-light-svg-icons/faBadgeCheck';
import { faClock } from '@fortawesome/pro-light-svg-icons/faClock';
import { ArtistScrapeStatus, ServiceConnectionStatus } from 'fym-shared/src/smartlinks/catalog';
import { musicServicesByMusicAPIID } from 'fym-shared/src/music-services/allMusicServices';
import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../music24/components/primitives/SMLButton';
import { ConnectionInsight, ServiceConnection } from '../../../redux/api/fymAPI.types';
import { ConnectionFollowers } from './ConnectionFollowers';
import { catalogServiceCardCopy } from './catalogServiceCards';
import { formatWaitingDays } from './catalogDates';
import { isServiceConnectOffered } from './catalogEnv';
import { ArtistPortalCardState, artistPortalCardState } from './artistPortalCardState';
import { ConnectionCardPortal } from './ConnectionCardPortal';
import { EarlyAccessServiceMark } from './earlyAccess/EarlyAccessServiceMark';
import { removeHttpAndSlash } from '../utils/removeHttpRegex';
import { YouTubeConnectionNote } from '../../music24/youtubeConnection/YouTubeConnectionNote';
import { SpotifyCanvasCardSwitch } from '../../music24/servicesPanel/spotifyCanvas/SpotifyCanvasCardSwitch';
import { spotifyCanvasSwitchMessages } from '../../music24/servicesPanel/spotifyCanvas/spotifyCanvasMessages';
import { withBrandWord } from '../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from './Catalog.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ConnectionCardProps {
  connection: ServiceConnection;
  /** The daily read of this service's profile, when there is one. */
  insight?: ConnectionInsight;
  onEditProfileUrl: (integrationType: string) => void;
  onConnect: (integrationType: string) => void;
  onSync: (integrationType: string) => void;
  onDisconnect: (integrationType: string) => void;
  /** True while any portal write is in flight, so the row cannot be double-tapped. */
  isBusy: boolean;
  /** Fixed by the screenshot build so two runs draw the same card. */
  now?: number;
  /** `canUseAutoUpload`: the Spotify for Artists card shows the Spotify Canvas switch (design b5). */
  showCanvasSwitch?: boolean;
}

/**
 * One service's standing. The dot and the pill say where the account is; the
 * buttons say what is still worth doing about it.
 */
const ConnectionCardComponent: React.FC<ConnectionCardProps> = ({
  connection,
  insight,
  onEditProfileUrl,
  onConnect,
  onSync,
  onDisconnect,
  isBusy,
  now: fixedNow,
  showCanvasSwitch = false,
}) => {
  const intl = useIntl();
  const { integrationType, status, artistProfileUrl, scrapeSupported, requestedAt } = connection;
  const copy = catalogServiceCardCopy[integrationType];
  const fallbackName = musicServicesByMusicAPIID[integrationType]?.name ?? integrationType;
  const name = copy ? intl.formatMessage(copy.programName) : fallbackName;
  // The Spotify Canvas switch box of the Spotify for Artists card (design spotify-canvas b5).
  const showsCanvas = integrationType === MusicAPIIntegrationID.Spotify && showCanvasSwitch;
  const serviceNote = copy ? intl.formatMessage(copy.note) : '';
  const note =
    showsCanvas && serviceNote !== ''
      ? `${serviceNote} ${intl.formatMessage(spotifyCanvasSwitchMessages.cardNote)}`
      : serviceNote;

  const portalState = artistPortalCardState(connection);
  const isConnected = status === ServiceConnectionStatus.connected;
  // A login that holds several artists is `connected` on the server, but
  // nothing is read until the artist picks one. The card must not say it is
  // verified yet.
  const needsArtist = portalState === ArtistPortalCardState.needsArtist;
  const isVerified = isConnected && !needsArtist;
  const isWaiting = status === ServiceConnectionStatus.requested || status === ServiceConnectionStatus.pending;
  const hasProfileUrl = artistProfileUrl !== null && artistProfileUrl !== '';

  const now = useMemo(() => Date.now(), []);

  const handle = useMemo(() => {
    if (isWaiting && requestedAt !== null) {
      return intl.formatMessage(
        { id: 'app.sml.catalog.connection.requested_for', defaultMessage: 'request sent · {duration}' },
        { duration: formatWaitingDays(intl, requestedAt, now) }
      );
    }
    if (hasProfileUrl) {
      return removeHttpAndSlash(artistProfileUrl);
    }
    if (needsArtist) {
      return intl.formatMessage({
        id: 'app.sml.catalog.connection.signed_in_choose_artist',
        defaultMessage: 'signed in · choose an artist',
      });
    }
    if (isConnected) {
      return intl.formatMessage({ id: 'app.sml.catalog.connection.connected', defaultMessage: 'connected' });
    }
    return intl.formatMessage({
      id: 'app.sml.catalog.connection.not_connected',
      defaultMessage: 'not connected',
    });
  }, [artistProfileUrl, hasProfileUrl, intl, isConnected, isWaiting, needsArtist, now, requestedAt]);

  /**
   * We hold a url and nothing reads it. That is usually a link shape we do not
   * know, and sometimes a registration that did not get through, so the note
   * says neither and offers another save — saving the same url is the retry.
   */
  const cannotRead = hasProfileUrl && (scrapeSupported === false || insight?.status === ArtistScrapeStatus.unsupported);

  const editProfileUrl = useCallback(() => onEditProfileUrl(integrationType), [integrationType, onEditProfileUrl]);
  const connect = useCallback(() => onConnect(integrationType), [integrationType, onConnect]);

  return (
    <div className={clsx(styles.connectionCard, !isVerified && styles.connectionCardOff)}>
      <EarlyAccessServiceMark integrationType={integrationType} />
      <div className={styles.connectionBody}>
        <div className={styles.connectionName}>
          <span
            className={clsx(
              styles.statusDot,
              isVerified && styles.statusDotOn,
              (isWaiting || needsArtist) && styles.statusDotPending
            )}
          />
          {name}
        </div>
        <div className={styles.connectionHandle}>{handle}</div>
        {insight !== undefined && <ConnectionFollowers insight={insight} />}
        {cannotRead && (
          <div className={styles.connectionSoftNote}>
            {intl.formatMessage({
              id: 'app.sml.catalog.insights.unreadable',
              defaultMessage: 'We could not read this link for daily stats. Check it and save it again.',
            })}
          </div>
        )}
        {note !== '' && <div className={styles.connectionNote}>{withBrandWord(note)}</div>}
        {showsCanvas && <SpotifyCanvasCardSwitch connection={connection} />}
        {integrationType === MusicAPIIntegrationID.YoutubeMusic && (
          // One YouTube sign-in for Streaming and Social. A card that is not connected keeps its
          // Connect button, so only a connected card gets the Reconnect button.
          <YouTubeConnectionNote
            gap={connection.reconnectToAdd}
            showReconnect={isConnected}
            disabled={isBusy}
            onReconnect={connect}
            noteClassName={styles.connectionNote}
            gapClassName={styles.connectionSoftNote}
            buttonClassName={styles.smallButton}
          />
        )}
        <ConnectionCardPortal
          connection={connection}
          isBusy={isBusy}
          now={fixedNow}
          onConnect={onConnect}
          onSync={onSync}
          onDisconnect={onDisconnect}
        />
        <div className={styles.connectionActions}>
          <SMLButton onClick={editProfileUrl} className={styles.smallButton}>
            {hasProfileUrl
              ? intl.formatMessage({ id: 'app.sml.catalog.connection.edit_url', defaultMessage: 'Edit URL' })
              : intl.formatMessage({ id: 'app.sml.catalog.connection.add_url', defaultMessage: 'Add profile URL' })}
          </SMLButton>
          {isServiceConnectOffered(connection) &&
            !isConnected &&
            !isWaiting &&
            portalState !== ArtistPortalCardState.expired && (
              <SMLButton variant="primary" onClick={connect} className={styles.smallButton}>
                {intl.formatMessage({ id: 'app.sml.catalog.connection.connect', defaultMessage: 'Connect' })}
              </SMLButton>
            )}
          {isVerified && (
            <span className={clsx(styles.pill, styles.pillVerified)}>
              <FontAwesomeIcon icon={faBadgeCheck} />
              {intl.formatMessage({ id: 'app.sml.catalog.connection.verified', defaultMessage: 'Verified' })}
            </span>
          )}
          {isWaiting && (
            <span className={clsx(styles.pill, styles.pillPending)}>
              <FontAwesomeIcon icon={faClock} />
              {status === ServiceConnectionStatus.pending
                ? intl.formatMessage({ id: 'app.sml.catalog.connection.pending', defaultMessage: 'Pending' })
                : intl.formatMessage({ id: 'app.sml.catalog.connection.requested', defaultMessage: 'Requested' })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export const ConnectionCard = TypedMemo(ConnectionCardComponent);
